import { DataObject, DataObjectFactory } from "@/v2/models/base-data-object.js";
import { Parameter } from "@/v2/models/parameters";
import { ProductFactory, VariableProduct, BundleProduct, ProductMapperParameter } from "@/v2/models/products.js";
import { DocumentLineItemFactory, DocumentLineItemMapperParameter } from "@/v2/models/documents.js";
import { MediaMapperParameter, MediaFactory } from "@/v2/models/medias";
import Decimal from "decimal.js";

export class Cart extends DataObject {
    _type = "cart";

    id;
    items = {};
    coupons = [];
    total_absolute_discount = 0;
    total_absolute_discount_percentage = 0;
    cart_discount_total = 0;
    shipping_fee = 0;
    attachments = {};
    return_attachments = {};
    remark;
    isTaxIncluded;
    showTax;
    mode;
    shipping_address;
    shipping_method;
    payment_mode;
    preferred_delivery_date;
    custom_fields;

    siteCurrency;
    siteCurrencyDecimalPlaces;
    shippingFeeTaxClass;

    getSubtotal() {
        if (this.siteCurrency === "TWD") {
            return this.getTwdSubtotalWithoutTax(false);
        }

        let total = 0;
        Object.values(this.items).forEach((item) => {
            if (item._type == "bundle_cart_item") {
                total += item.getDisplayTotal();
            } else {
                total += item.getTotal();
            }
        });
        return total;
    }

    getSubtotalWithTaxForShippingCalculation() {
        let total = 0;
        if (this.siteCurrency === "TWD") {
            total = this.getTwdSubtotalWithoutTax(false);

            if (!this.showTax) {
                Object.values(this.getTaxes(false)).forEach((taxTotal) => {
                    total += taxTotal;
                });
            }
        } else {
            Object.values(this.items).forEach((item) => {
                total += item.getTotalWithTax();

                if (item._type == "bundle_cart_item" && item.showTax) {
                    total += item.getTotalTax();
                }
            });
            total -= this.getCartDiscountWithTax();
        }
        return total;
    }

    getSubtotalWithoutBundle() {
        let total = 0;
        Object.values(this.items).forEach((item) => {
            if (item._type == "variable_cart_item") {
                total += item.getTotal();
            }
        });
        return total;
    }

    getTotal() {
        if (this.siteCurrency === "TWD") {
            return this.getSubtotal() + this.shipping_fee;
        }
        return this.getSubtotal() - this.getCartDiscount() + this.shipping_fee;
    }

    getTotalWithTax() {
        let total = 0;
        if (this.siteCurrency === "TWD") {
            total = this.getTwdSubtotalWithoutTax(true);

            if (!this.showTax) {
                Object.values(this.getTaxes()).forEach((taxTotal) => {
                    total += taxTotal;
                });
            }
        } else {
            Object.values(this.items).forEach((item) => {
                total += item.getTotalWithTax();

                if (item._type == "bundle_cart_item" && item.showTax) {
                    total += item.getTotalTax();
                }
            });
            total -= this.getCartDiscountWithTax();

            let shippingFeeTax = 0;
            if (!this.showTax) {
                shippingFeeTax = this.getTax(this.shipping_fee, this.shippingFeeTaxClass);
            }
            total += this.shipping_fee + shippingFeeTax;
        }
        return total;
    }

    getSubtotalWithTax() {
        let subtotal = 0;
        Object.values(this.items).forEach((item) => (subtotal += item.getSubtotal(true)));
        return subtotal;
    }

    getQuantity() {
        let total = 0;
        Object.values(this.items).forEach((item) => (total += item.quantity));
        return total;
    }

    getItemParent(itemId) {
        const item = this.items[itemId];
        if (item == null) return null;
        return this.items[item.composite_parent_id];
    }

    getItemChildren(itemId) {
        const item = this.items[itemId];
        if (item == null || !item instanceof BundleCartItem) return null;
        let children = [];
        for (const child in this.items) {
            if (child.id === item.composite_parent_id) {
                children.push(child);
            }
        }
        return children;
    }

    // coupon will be apply if the subtotal grater or equal to coupon min document amount
    setCoupons(coupons) {
        this.coupons = [];
        this.coupons.push(...coupons);

        // reset product price
        Object.values(this.items).forEach((item) => {
            if (item._type == "variable_cart_item") {
                item.absolute_discount_percentage = 100;
                item.absolute_adjustment = 0;
                item.discount_percentage = 100;

                item.couponIds = [];
                item.absoluteCouponIds = [];
            } else if (item._type == "bundle_cart_item") {
                Object.values(item.composite_children).forEach((compositeChildren) => {
                    compositeChildren.absolute_discount_percentage = 100;
                    compositeChildren.absolute_adjustment = 0;
                    compositeChildren.discount_percentage = 100;

                    compositeChildren.couponIds = [];
                    compositeChildren.absoluteCouponIds = [];
                });
            }
        });

        Object.values(this.coupons).forEach((coupon) => {
            let couponType = coupon.type;
            // set current total ampunt
            let currentTotal = 0;
            Object.values(this.items).forEach((item) => {
                if (couponType == "cart_discount") {
                    if (item._type == "variable_cart_item") {
                        currentTotal += item.getTotal();
                    } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                        Object.values(item.composite_children).forEach(
                            (compositeChildren) => (currentTotal += compositeChildren.getTotal())
                        );
                    }
                } else {
                    if (item._type == "variable_cart_item") {
                        let isProductExist = coupon.coupon_products.some(function (product) {
                            return item.product_id == product.id;
                        });
                        if (isProductExist) {
                            currentTotal += item.getTotal();
                            return;
                        }

                        let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                            item.product.groups.some(function (group) {
                                return group.id == product_group.id;
                            });
                        });
                        if (isProductGroupExist) {
                            currentTotal += item.getTotal();
                            return;
                        }
                    } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                        Object.values(item.composite_children).forEach((compositeChildren) => {
                            let isProductExist = coupon.coupon_products.some(function (product) {
                                return compositeChildren.product_id == product.id;
                            });
                            if (isProductExist) {
                                currentTotal += compositeChildren.getTotal();
                                return;
                            }

                            let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                                compositeChildren.product.groups.some(function (group) {
                                    return group.id == product_group.id;
                                });
                            });
                            if (isProductGroupExist) {
                                currentTotal += compositeChildren.getTotal();
                                return;
                            }
                        });
                    }
                }
            });
            coupon.currentTotal = currentTotal;

            // set current purchase quantity
            let count = 0;
            Object.values(this.items).forEach((item) => {
                if (couponType == "cart_discount") {
                    if (item._type == "variable_cart_item") {
                        count += item.quantity;
                    } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                        Object.values(item.composite_children).forEach(
                            (compositeChildren) =>
                                (count += compositeChildren.quantity * compositeChildren.composite_parent_quantity)
                        );
                    }
                } else {
                    if (item._type == "variable_cart_item") {
                        let isProductExist = coupon.coupon_products.some(function (product) {
                            return item.product_id == product.id;
                        });
                        if (isProductExist) {
                            count += item.quantity;
                            return;
                        }

                        let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                            item.product.groups.some(function (group) {
                                return group.id == product_group.id;
                            });
                        });
                        if (isProductGroupExist) {
                            count += item.quantity;
                            return;
                        }
                    } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                        Object.values(item.composite_children).forEach((compositeChildren) => {
                            let isProductExist = coupon.coupon_products.some(function (product) {
                                return compositeChildren.product_id == product.id;
                            });
                            if (isProductExist) {
                                count += compositeChildren.quantity * compositeChildren.composite_parent_quantity;
                                return;
                            }

                            let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                                compositeChildren.product.groups.some(function (group) {
                                    return group.id == product_group.id;
                                });
                            });
                            if (isProductGroupExist) {
                                count += compositeChildren.quantity * compositeChildren.composite_parent_quantity;
                                return;
                            }
                        });
                    }
                }
            });
            coupon.currentPurchaseQuantity = count;
        });

        Object.values(this.items).forEach((item) => {
            item.discount_percentage = 100;
            item.couponIds = [];
            item.absoluteCouponIds = [];
            Object.values(this.coupons).forEach((coupon) => {
                // if (coupon.discount_type == "percentage") {
                coupon.discount_amount = 0;

                if (coupon.are_bundles_excluded) {
                    if (item._type == "bundle_cart_item") return;
                }

                // check min_document_amount
                if (coupon.min_document_amount > coupon.currentTotal) return;

                // check purchase_quantity
                const maximumProductQuantity =
                    coupon.variation_quantity_remaining ??
                    coupon.variation_quantity_limit ??
                    coupon.remaining_product_quantity ??
                    coupon.maximum_product_quantity;
                if (
                    maximumProductQuantity != null &&
                    coupon.currentPurchaseQuantity > maximumProductQuantity
                ) {
                    return;
                }

                if (
                    coupon.minimum_product_quantity != null &&
                    coupon.currentPurchaseQuantity < coupon.minimum_product_quantity
                ) {
                    return;
                }

                if (coupon.type == "cart_discount") {
                    // Booked as its own line in applyCartDiscountLine() instead.
                    if (this.isCartDiscountLineCoupon(coupon)) return;

                    if (item._type == "variable_cart_item") {
                        if (coupon.discount_type == "percentage") {
                            item.couponIds.push(coupon.id);
                        } else {
                            item.absoluteCouponIds.push(coupon.id);
                        }
                    } else if (item._type == "bundle_cart_item") {
                        Object.values(item.composite_children).forEach((compositeChildren) => {
                            if (coupon.discount_type == "percentage") {
                                compositeChildren.couponIds.push(coupon.id);
                            } else {
                                compositeChildren.absoluteCouponIds.push(coupon.id);
                            }
                        });
                    }
                } else {
                    if (item._type == "variable_cart_item") {
                        let isProductExist = coupon.coupon_products.some(function (product) {
                            return item.product_id == product.id;
                        });
                        if (isProductExist) {
                            if (coupon.discount_type == "percentage") {
                                item.couponIds.push(coupon.id);
                            } else {
                                item.absoluteCouponIds.push(coupon.id);
                            }
                            return;
                        }

                        let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                            item.product.groups.some(function (group) {
                                return group.id == product_group.id;
                            });
                        });
                        if (isProductGroupExist) {
                            if (coupon.discount_type == "percentage") {
                                item.couponIds.push(coupon.id);
                            } else {
                                item.absoluteCouponIds.push(coupon.id);
                            }
                            return;
                        }
                    } else if (item._type == "bundle_cart_item") {
                        Object.values(item.composite_children).forEach((compositeChildren) => {
                            let isProductExist = coupon.coupon_products.some(function (product) {
                                return compositeChildren.product_id == product.id;
                            });
                            if (isProductExist) {
                                if (coupon.discount_type == "percentage") {
                                    compositeChildren.couponIds.push(coupon.id);
                                } else {
                                    compositeChildren.absoluteCouponIds.push(coupon.id);
                                }
                                return;
                            }

                            let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                                compositeChildren.product.groups.some(function (group) {
                                    return group.id == product_group.id;
                                });
                            });
                            if (isProductGroupExist) {
                                if (coupon.discount_type == "percentage") {
                                    compositeChildren.couponIds.push(coupon.id);
                                } else {
                                    compositeChildren.absoluteCouponIds.push(coupon.id);
                                }
                                return;
                            }
                        });
                    }
                }
                // }
            });
        });
        this.applyAbsoluteCoupon();
        this.applyDiscount();
        this.applyCartDiscountLine();

        return this.coupons;
    }

    /**
     * With a cart discount SKU configured, a cart discount coupon becomes a negative line item on the
     * order rather than being spread over the items, so the items keep their prices here.
     */
    usesCartDiscountLine() {
        return window.bo?.cartDiscountProduct != null;
    }

    isCartDiscountLineCoupon(coupon) {
        return coupon.type == "cart_discount" && this.usesCartDiscountLine();
    }

    getCartDiscountTaxClass() {
        return window.bo?.cartDiscountProduct?.tax_class ?? null;
    }

    getCartDiscount() {
        return this.cart_discount_total;
    }

    getCartDiscountWithTax() {
        if (this.showTax) {
            return this.cart_discount_total;
        }
        return this.cart_discount_total + this.getTax(this.cart_discount_total, this.getCartDiscountTaxClass());
    }

    isCouponEligible(coupon) {
        if (coupon.min_document_amount > coupon.currentTotal) return false;

        const maximumProductQuantity =
            coupon.variation_quantity_remaining ??
            coupon.variation_quantity_limit ??
            coupon.remaining_product_quantity ??
            coupon.maximum_product_quantity;
        if (maximumProductQuantity != null && coupon.currentPurchaseQuantity > maximumProductQuantity) {
            return false;
        }

        if (coupon.minimum_product_quantity != null && coupon.currentPurchaseQuantity < coupon.minimum_product_quantity) {
            return false;
        }

        return true;
    }

    // Item totals after product-level coupons, which is what a cart discount is taken off.
    getCartDiscountBase(coupon) {
        let total = 0;
        Object.values(this.items).forEach((item) => {
            if (item._type == "variable_cart_item") {
                total += item.getTotal();
            } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                Object.values(item.composite_children).forEach(
                    (compositeChildren) => (total += compositeChildren.getTotal())
                );
            }
        });
        return total;
    }

    // Amounts are absolute first, then percentage on what is left, matching the order used when spread.
    applyCartDiscountLine() {
        this.cart_discount_total = 0;
        if (!this.usesCartDiscountLine()) return;

        const coupons = Object.values(this.coupons).filter(
            (coupon) => this.isCartDiscountLineCoupon(coupon) && this.isCouponEligible(coupon)
        );
        const orderedCoupons = [
            ...coupons.filter((coupon) => coupon.discount_type == "unit_amount"),
            ...coupons.filter((coupon) => coupon.discount_type != "unit_amount"),
        ];

        orderedCoupons.forEach((coupon) => {
            const remaining = Math.max(this.getCartDiscountBase(coupon) - this.cart_discount_total, 0);
            let amount =
                coupon.discount_type == "unit_amount"
                    ? Math.min(parseFloat(coupon.discount), remaining)
                    : (remaining * parseFloat(coupon.discount)) / 100;
            amount = new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();

            coupon.discount_amount = amount;
            coupon.applied_discount_amount = amount;
            this.cart_discount_total += amount;
        });
    }

    applyAbsoluteCoupon() {
        this.total_absolute_discount = 0;
        let [item] = Object.values(this.items);
        if (item) {
            item.absolute_adjustment = 0;
        }

        // Object.values(this.items).forEach((item) => {
        //     item.absolute_discount_percentage = 100;
        //     item.absolute_adjustment = 0;
        //     item.discount_percentage = 100;
        // });

        Object.values(this.coupons).forEach((coupon) => {
            if (coupon.discount_type == "unit_amount" && !this.isCartDiscountLineCoupon(coupon)) {
                // check min_document_amount
                if (coupon.min_document_amount > coupon.currentTotal) return;

                // check purchase_quantity
                const maximumProductQuantity =
                    coupon.variation_quantity_remaining ??
                    coupon.variation_quantity_limit ??
                    coupon.remaining_product_quantity ??
                    coupon.maximum_product_quantity;
                if (
                    maximumProductQuantity != null &&
                    coupon.currentPurchaseQuantity > maximumProductQuantity
                ) {
                    return;
                }

                if (
                    coupon.minimum_product_quantity != null &&
                    coupon.currentPurchaseQuantity < coupon.minimum_product_quantity
                ) {
                    return;
                }

                coupon.applied_discount_amount =
                    coupon.promotion_id != null
                        ? Math.min(
                              parseFloat(coupon.discount) * coupon.currentPurchaseQuantity,
                              coupon.currentTotal
                          )
                        : parseFloat(coupon.discount);
                this.total_absolute_discount += coupon.applied_discount_amount;

                let currentTotalForCalculatingDiscount = 0;
                let couponType = coupon.type;
                Object.values(this.items).forEach((item) => {
                    if (couponType == "cart_discount") {
                        if (item._type == "variable_cart_item") {
                            currentTotalForCalculatingDiscount += item.getTotal();
                        } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                            Object.values(item.composite_children).forEach(
                                (compositeChildren) =>
                                    (currentTotalForCalculatingDiscount += compositeChildren.getTotal())
                            );
                        }
                    } else {
                        if (item._type == "variable_cart_item") {
                            let isProductExist = coupon.coupon_products.some(function (product) {
                                return item.product_id == product.id;
                            });
                            if (isProductExist) {
                                currentTotalForCalculatingDiscount += item.getTotal();
                                return;
                            }

                            let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                                item.product.groups.some(function (group) {
                                    return group.id == product_group.id;
                                });
                            });
                            if (isProductGroupExist) {
                                currentTotalForCalculatingDiscount += item.getTotal();
                                return;
                            }
                        } else if (item._type == "bundle_cart_item" && !coupon.are_bundles_excluded) {
                            Object.values(item.composite_children).forEach((compositeChildren) => {
                                let isProductExist = coupon.coupon_products.some(function (product) {
                                    return compositeChildren.product_id == product.id;
                                });
                                if (isProductExist) {
                                    currentTotalForCalculatingDiscount += compositeChildren.getTotal();
                                    return;
                                }

                                let isProductGroupExist = coupon.coupon_product_groups.some(function (product_group) {
                                    compositeChildren.product.groups.some(function (group) {
                                        return group.id == product_group.id;
                                    });
                                });
                                if (isProductGroupExist) {
                                    currentTotalForCalculatingDiscount += compositeChildren.getTotal();
                                    return;
                                }
                            });
                        }
                    }
                });
                let absoluteDiscountPercentage =
                    (currentTotalForCalculatingDiscount - coupon.applied_discount_amount) /
                    currentTotalForCalculatingDiscount;
                if (absoluteDiscountPercentage < 0) absoluteDiscountPercentage = 0;

                Object.values(this.items).forEach((item) => {
                    if (item._type == "variable_cart_item") {
                        if (!item.absoluteCouponIds.includes(coupon.id)) return;
                        item.absolute_discount_percentage *= absoluteDiscountPercentage;
                    } else if (item._type == "bundle_cart_item") {
                        Object.values(item.composite_children).forEach((compositeChildren) => {
                            if (!compositeChildren.absoluteCouponIds.includes(coupon.id)) return;
                            compositeChildren.absolute_discount_percentage *= absoluteDiscountPercentage;
                        });
                    }
                });
            }
        });

        // this.calculateAndInsertAbsolutePercentage();
        if (item) {
            item.absolute_adjustment = this.calculateAbsoluteAdjustment();
        }
    }

    // calculateAndInsertAbsolutePercentage() {
    //     this.total_absolute_discount_percentage =
    //         (this.getSubtotalWithoutBundle() - this.total_absolute_discount) / this.getSubtotalWithoutBundle();
    //     if (this.total_absolute_discount_percentage < 0) this.total_absolute_discount_percentage = 0;

    //     Object.values(this.items).forEach((item) => {
    //         if (item._type == "variable_cart_item") {
    //             item.absolute_discount_percentage *= this.total_absolute_discount_percentage;
    //         }
    //     });
    // }

    calculateAbsoluteAdjustment() {
        let totalAbsoluteDiscountApply = 0;
        Object.values(this.items).forEach((item) => {
            if (item._type == "variable_cart_item") {
                totalAbsoluteDiscountApply +=
                    (item.getCurrentPrice() - item.getAbsoluteDiscountPrice()) * item.quantity;
                if (item.getAbsoluteDiscountPrice() == 0) {
                    this.total_absolute_discount = totalAbsoluteDiscountApply;
                    return;
                }
            } else if (item._type == "bundle_cart_item") {
                Object.values(item.composite_children).forEach((compositeChildren) => {
                    totalAbsoluteDiscountApply +=
                        (compositeChildren.getCurrentPrice() - compositeChildren.getAbsoluteDiscountPrice()) *
                        compositeChildren.quantity *
                        compositeChildren.composite_parent_quantity;
                    if (compositeChildren.getAbsoluteDiscountPrice() == 0) {
                        this.total_absolute_discount = totalAbsoluteDiscountApply;
                        return;
                    }
                });
            }
        });
        return this.total_absolute_discount - totalAbsoluteDiscountApply;
    }

    // calculateAbsoluteTotalDiscount() {
    //     Object.values(this.items).forEach((item) => {});
    // }

    applyDiscount() {
        Object.values(this.items).forEach((item) => {
            item.discount_percentage = 100;
            Object.values(this.coupons).forEach((coupon) => {
                if (coupon.discount_type == "unit_amount") return;
                if (item._type == "variable_cart_item") {
                    if (!item.couponIds.includes(coupon.id)) return;
                    const priceBeforeDiscount = item.getPrice();
                    item.discount_percentage = (item.discount_percentage * (100 - parseFloat(coupon.discount))) / 100;
                    coupon.discount_amount += (priceBeforeDiscount - item.getPrice()) * item.quantity;
                } else if (item._type == "bundle_cart_item") {
                    Object.values(item.composite_children).forEach((compositeChildren) => {
                        if (!compositeChildren.couponIds.includes(coupon.id)) return;
                        const priceBeforeDiscount = compositeChildren.getPrice();
                        compositeChildren.discount_percentage =
                            (compositeChildren.discount_percentage * (100 - parseFloat(coupon.discount))) / 100;
                        coupon.discount_amount +=
                            (priceBeforeDiscount - compositeChildren.getPrice()) *
                            compositeChildren.quantity *
                            compositeChildren.composite_parent_quantity;
                    });
                }
            });
        });
    }

    // calculateDiscount() {
    //     // loop cart item set discount
    //     // calculate general discount first
    //     // add on special discount
    //     if (this.coupons.length == 0) return;

    //     let discount_percentage = 100;
    //     let discount = 0;
    //     let productCoupons = [];

    //     Object.values(this.coupons).forEach((coupon) => {
    //         if (coupon.type == "cart_discount") {
    //             if (coupon.discount_type == "percentage") {
    //                 discount_percentage =
    //                     (discount_percentage *
    //                         (100 - parseFloat(coupon.discount))) /
    //                     100;
    //             } else if (coupon.discount_type == "unit_amount") {
    //                 discount += coupon.discount;
    //             }
    //         } else {
    //             productCoupons.push(coupon);
    //         }
    //     })

    //     if (discount > 0) {
    //         discount_percentage = ((this.getSubtotal() - discount) / this.getSubtotal()) * discount_percentage;
    //     }

    //     Object.values(this.items).forEach((item) => {
    //         item.discount_percentage = discount_percentage;
    //         Object.values(productCoupons).forEach((productCoupon) => {
    //             if (productCoupon.products.has(item.product_id)) {
    //                 item.discount_percentage =
    //                     (item.discount_percentage *
    //                         (100 - parseFloat(productCoupon.discount))) /
    //                     100;
    //                 if (productCoupon.type == "product_discount_on_msrp") {
    //                     item.is_msrp = true;
    //                 }
    //             }
    //         });
    //     });

    // }

    // getCouponDiscountAmount() {
    //     let totalDiscountAmount = 0;
    //     Object.values(this.coupons).forEach((coupon) => {
    //         if (coupon.discount_type == "unit_amount") {
    //             coupon.discount_amount = coupon.discount;
    //         }
    //         else {
    //             if (coupon.type == "cart_discount") {
    //                 coupon.discount_amount = (this.getSubtotal() - totalDiscountAmount) * coupon.discount;
    //             }
    //             else {
    //                 coupon.discount_amount = 0;
    //                 Object.values(this.items).forEach((item) => {

    //                 })
    //             }
    //         }
    //         totalDiscountAmount += coupon.discount_amount;
    //     })
    // }

    /**
     * Groups TWD item/shipping totals by tax class, rounding each group's subtotal once (matches
     * Lingyue) before computing tax on it.
     */
    getTwdTaxClassGroups(includeShippingFee = true) {
        let groups = {};
        if (this.items != null) {
            Object.values(this.items).forEach((item) => {
                if (item._type == "bundle_cart_item" && !item.isTaxExempted) {
                    Object.values(item.composite_children).forEach((composite_child) => {
                        if (composite_child.tax_class) {
                            if (!groups[composite_child.tax_class.name]) {
                                groups[composite_child.tax_class.name] = {
                                    total: 0,
                                    tax_class: composite_child.tax_class,
                                };
                            }

                            groups[composite_child.tax_class.name].total += composite_child.getTotal(item);
                        }
                    });
                } else if (!item.isTaxExempted) {
                    let taxClass = this.getItemTaxClass(item);

                    if (taxClass) {
                        if (!groups[taxClass.name]) {
                            groups[taxClass.name] = {
                                total: 0,
                                tax_class: taxClass,
                            };
                        }

                        groups[taxClass.name].total += item.getTotal();
                    }
                }
            });
        }

        const cartDiscountTaxClass = this.getCartDiscountTaxClass();
        if (this.getCartDiscount() > 0 && cartDiscountTaxClass && !this.taxExempted) {
            if (!groups[cartDiscountTaxClass.name]) {
                groups[cartDiscountTaxClass.name] = {
                    total: 0,
                    tax_class: cartDiscountTaxClass,
                };
            }
            groups[cartDiscountTaxClass.name].total -= this.getCartDiscount();
        }

        if (includeShippingFee) {
            let shippingFeeTaxClass = this.shippingFeeTaxClass;
            if (shippingFeeTaxClass !== null && typeof shippingFeeTaxClass !== "undefined") {
                if (!groups[shippingFeeTaxClass.name]) {
                    groups[shippingFeeTaxClass.name] = {
                        total: 0,
                        tax_class: shippingFeeTaxClass,
                    };
                }
                groups[shippingFeeTaxClass.name].total += this.shipping_fee;
            }
        }

        let result = {};
        Object.entries(groups).forEach(([taxClass, info]) => {
            let subtotal = Math.round(info.total);
            result[taxClass] = {
                subtotal: subtotal,
                tax: Math.round(this.getTax(subtotal, info.tax_class)),
            };
        });
        return result;
    }

    /**
     * Sum of the cart's pre-tax total for TWD: each tax class group's once-rounded subtotal, plus
     * untaxed items/shipping summed and rounded once as their own implicit group.
     */
    getTwdSubtotalWithoutTax(includeShippingFee = true) {
        let groups = this.getTwdTaxClassGroups(includeShippingFee);
        let total = 0;
        Object.values(groups).forEach((info) => {
            total += info.subtotal;
        });

        // Tax-exempted items, items/shipping without a tax class, and untaxed bundle composite
        // children are accumulated here and rounded once as their own implicit group.
        let untaxedTotal = 0;
        if (this.items != null) {
            Object.values(this.items).forEach((item) => {
                if (item._type == "bundle_cart_item" && !item.isTaxExempted) {
                    Object.values(item.composite_children).forEach((composite_child) => {
                        if (!composite_child.tax_class) {
                            untaxedTotal += composite_child.getTotal(item);
                        }
                    });
                } else if (item.isTaxExempted || !this.getItemTaxClass(item)) {
                    untaxedTotal += item.getTotal();
                }
            });
        }

        if (includeShippingFee && (this.shippingFeeTaxClass === null || typeof this.shippingFeeTaxClass === "undefined")) {
            untaxedTotal += this.shipping_fee;
        }

        if (!this.getCartDiscountTaxClass() || this.taxExempted) {
            untaxedTotal -= this.getCartDiscount();
        }

        total += Math.round(untaxedTotal);

        return total;
    }

    getTaxes(includeShippingFee = true) {
        let taxes = {};
        if (this.siteCurrency === "TWD") {
            Object.entries(this.getTwdTaxClassGroups(includeShippingFee)).forEach(([taxClass, info]) => {
                taxes[taxClass] = info.tax;
            });
        } else {
            if (this.items != null) {
                Object.values(this.items).forEach((item) => {
                    if (item._type == "bundle_cart_item" && !item.isTaxExempted) {
                        Object.values(item.composite_children).forEach((composite_child) => {
                            if (composite_child.tax_class) {
                                let tax = composite_child.getTotalTax() * item.quantity;
                                taxes[composite_child.tax_class.name] = taxes[composite_child.tax_class.name]
                                    ? taxes[composite_child.tax_class.name] + tax
                                    : tax;
                            }
                        });
                    } else if (!item.isTaxExempted) {
                        let taxClass = this.getItemTaxClass(item);

                        if (taxClass) {
                            taxes[taxClass.name] = taxes[taxClass.name]
                                ? taxes[taxClass.name] + item.getTotalTax()
                                : item.getTotalTax();
                        }
                    }
                });

                const cartDiscountTaxClass = this.getCartDiscountTaxClass();
                if (this.getCartDiscount() > 0 && cartDiscountTaxClass) {
                    taxes[cartDiscountTaxClass.name] =
                        (taxes[cartDiscountTaxClass.name] ?? 0) -
                        this.getTax(this.getCartDiscount(), cartDiscountTaxClass);
                }

                if (includeShippingFee) {
                    let shippingFeeTaxClass = this.shippingFeeTaxClass;
                    if (shippingFeeTaxClass !== null && typeof shippingFeeTaxClass !== "undefined") {
                        taxes[shippingFeeTaxClass.name] = taxes[shippingFeeTaxClass.name]
                            ? taxes[shippingFeeTaxClass.name] + this.getTax(this.shipping_fee, shippingFeeTaxClass)
                            : this.getTax(this.shipping_fee, shippingFeeTaxClass);
                    }
                }
            }
        }
        return taxes;
    }

    getTax(price, taxClass) {
        if (!taxClass || this.taxExempted) {
            return 0;
        }
        if (this.showTax) {
            return (taxClass.percentage / 100) * (price / (1 + taxClass.percentage / 100));
        }

        return (taxClass.percentage / 100) * price;
    }

    addOrRemoveTaxIfNeeded(taxClass, price) {
        if (this.taxExempted) {
            if (this.isTaxIncluded && taxClass) {
                return price / (1 + taxClass.percentage / 100);
            } else {
                return price;
            }
        } else {
            if (this.isTaxIncluded == this.showTax || !taxClass) {
                return price;
            }
            if (this.isTaxIncluded && !this.showTax) {
                return price / (1 + taxClass.percentage / 100);
            }
            if (!this.isTaxIncluded && this.showTax) {
                return price * (1 + taxClass.percentage / 100);
            }
        }
    }

    getItemTaxClass(item) {
        if (window.bo.enableQuotationTransferToSalesOrder) {
            return item.line_item?.tax_class;
        }
        if (item.mode === "return" && item.line_item?.tax_class) {
            return item.line_item.tax_class;
        }
        return item.product.tax_class;
    }
}

export class CartItem extends DataObject {
    _type = "cart_item";

    id;
    line_item_id = null;
    document_number;
    line_item;
    product_id;
    product;
    quantity;
    discount_percentage = 100;
    is_msrp = false;
    effective_price;
    price;
    total;
    subtotal;
    total_tax;
    subtotal_tax;
    company_id;
    couponIds = [];
    absoluteCouponIds = [];
    absolute_adjustment = 0;
    absolrn_reason;
    seriute_discount_percentage = 100;
    absolute_discount_percentage = 100;
    retual_numbers = [];
    batch;
    return_reason;
    serial_numbers = [];
    remark;
    isTaxIncluded;
    showTax;
    shipping_address;
    isTaxExempted;
    mode;
    product_details_url;
    isFreeQuantity = false;
    has_volume_pricing = false;

    tax_class;
    regular_price;
    regular_tax;
    unit_price;
    unit_tax;
    display_regular_price; // base price displayed
    display_regular_tax; // base tax displayed
    display_unit_price; // price displayed after discount & tax
    display_unit_tax; // price displayed after discount & tax
    display_subtotal; // calc subtotal after tax
    display_subtotal_tax; // calc subtotal tax
    display_total; // calc total after tax
    display_total_tax; // calc total tax

    siteCurrency;
    siteCurrencyDecimalPlaces;
    roundLineItemDiscount;

    getWeight() {
        return this.product.getWeight();
    }

    getVolume() {
        return this.product.getVolume();
    }

    getDimensionalSum() {
        return this.product.getDimensionalSum();
    }

    getPrice() {
        let price = 0;
        if (this.is_msrp) {
            price = (this.getOriginalPrice() * this.discount_percentage) / 100;
        } else {
            price = (this.getAbsoluteDiscountPrice() * this.discount_percentage) / 100;
        }

        if (this.siteCurrency === "TWD") {
            price = Math.round(price * 100) / 100;
        }
        return price;
    }

    getAbsoluteDiscountPrice() {
        return (
            parseFloat((this.getCurrentPrice() * this.absolute_discount_percentage) / 100) -
            this.absolute_adjustment / (this.quantity ? this.quantity : 1)
        );
    }

    // getEffectivePrice() {
    //     if (this.is_msrp) {
    //         return (this.getOriginalPrice() * this.discount_percentage) / 100;
    //     }
    //     let effectivePriceAfterAbosoluteDiscount =
    //         parseFloat(
    //             (
    //                 (this.price * this.absolute_discount_percentage) /
    //                 100
    //             ).toFixed(2)
    //         ) -
    //         this.absolute_adjustment / (this.quantity ? this.quantity : 1);
    //     return (
    //         (effectivePriceAfterAbosoluteDiscount * this.discount_percentage) /
    //         100
    //     );
    // }

    getCurrentPrice() {
        if (this.line_item) {
            if (this.mode == "return") {
                return parseFloat(this.addOrRemoveTaxIfNeeded(this.line_item.price));
            } else {
                return this.line_item.price;
            }
        }
        return parseFloat(this.addOrRemoveTaxIfNeeded(this.product.getCurrentPrice()));
    }

    getOriginalPrice() {
        if (this.line_item) {
            if (this.mode == "return") {
                return parseFloat(this.addOrRemoveTaxIfNeeded(this.line_item.price));
            } else {
                return this.line_item.price;
            }
        }
        return parseFloat(this.addOrRemoveTaxIfNeeded(this.product.getOriginalPrice()));
    }

    getTaxClass() {
        if (this.line_item) {
            return this.line_item.tax_class;
        }

        return this.product.tax_class;
    }

    addOrRemoveTaxIfNeeded(price) {
        if (this.isTaxExempted) {
            if (this.isTaxIncluded && this.getTaxClass()) {
                return price / (1 + this.getTaxClass().percentage / 100);
            } else {
                return price;
            }
        } else {
            if (
                this.isTaxIncluded == this.showTax ||
                !this.getTaxClass() ||
                (this.product.type == "bundle" && this.product.per_item_pricing)
            ) {
                return price;
            } else if (!this.isTaxIncluded && this.showTax) {
                return price * (1 + this.getTaxClass().percentage / 100);
            } else if (this.isTaxIncluded && !this.showTax) {
                return price / (1 + this.getTaxClass().percentage / 100);
            }
        }
    }

    removeTaxIfNeeded(price) {
        if (
            (this.isTaxIncluded &&
                this.showTax &&
                this.getTaxClass() &&
                !(this.product.type == "bundle" && this.product.per_item_pricing)) ||
            (this.isTaxIncluded && this.isTaxExempted && this.getTaxClass()) ||
            (this.showTax && this.getTaxClass())
        ) {
            return price / (1 + this.getTaxClass().percentage / 100);
        }
        return price;
    }

    getTotalTax() {
        if ((!this.isTaxExempted || this.line_item_id) && this.getTaxClass()) {
            if (this.getTaxClass() && !(this.product.type == "bundle" && this.product.per_item_pricing)) {
                if (this.showTax) {
                    return (
                        (this.getTotal() / (1 + this.getTaxClass().percentage / 100)) *
                        (this.getTaxClass().percentage / 100)
                    );
                    // if (this.isTaxIncluded) {
                    // } else {
                    //     return (this.getTotal() * this.getTaxClass().percentage) / 100;
                    // }
                }

                return this.getTotal() * (this.getTaxClass().percentage / 100);
            }
        }

        return 0;
    }

    getSubtotalTax() {
        if (!this.isTaxExempted && this.getTaxClass()) {
            if (this.getTaxClass() && !(this.product.type == "bundle" && this.product.per_item_pricing)) {
                if (this.showTax) {
                    return (
                        (this.getSubtotal() / (1 + this.getTaxClass().percentage / 100)) *
                        (this.getTaxClass().percentage / 100)
                    );
                    // if (this.isTaxIncluded) {
                    // } else {
                    //     return (this.getSubtotal() * this.getTaxClass().percentage) / 100;
                    // }
                }

                return this.getSubtotal() * (this.getTaxClass().percentage / 100);
            }
        }

        return 0;
    }

    getTotalWithTax() {
        if (this.showTax) {
            return this.getTotal();
        }
        return this.getTotal() + this.getTotalTax();
    }

    getTotal() {
        // change total to round(subtotal - discount)
        let originalPrice = new Decimal(this.getOriginalPrice());
        let subtotal = originalPrice.mul(this.quantity);
        let total;
        const formula = this.product.getDiscountFormula();

        if (formula) {
            const discount = this.calculateComplexDiscount(formula, subtotal);
            // const totalDiscount = discount.mul(this.quantity);
            total = subtotal.minus(discount);
        } else {
            total = new Decimal(this.getPrice()).mul(this.quantity);
        }

        // Item totals round to 2dp for every currency, matching the cart_items DB column
        // (decimal(14,2)).
        const itemTotalDecimalPlaces = 2;
        total = total.toDecimalPlaces(itemTotalDecimalPlaces, Decimal.ROUND_HALF_UP);

        return total.toNumber();
    }

    // for variable
    getDisplayTotal() {
        return this.getTotal();
    }

    getTotalWithoutTax() {
        let totalWithoutTax = 0;
        if (this.isTaxExempted) {
            totalWithoutTax = this.getTotal();
        } else {
            totalWithoutTax = this.removeTaxIfNeeded(this.getTotal());
        }

        if (this.siteCurrency === "TWD") {
            totalWithoutTax = Math.round(totalWithoutTax * 100) / 100;
        }
        return totalWithoutTax;
    }

    getSubtotalWithoutTax() {
        let subtotalWithoutTax = 0;
        if (this.isTaxExempted) {
            subtotalWithoutTax = this.getSubtotal();
        } else {
            subtotalWithoutTax = this.removeTaxIfNeeded(this.getSubtotal());
        }

        if (this.siteCurrency === "TWD") {
            subtotalWithoutTax = Math.round(subtotalWithoutTax * 100) / 100;
        }
        return subtotalWithoutTax;
    }

    roundToDecimal(number, decimal = 2) {
        if (typeof number == "string") {
            return parseFloat(number.toFixed(decimal));
        } else {
            return number.toFixed(decimal);
        }
    }

    getSubtotal(isTradeAgreementPricing = false) {
        let subtotal = 0;
        if (isTradeAgreementPricing) {
            subtotal = this.getPrice() * this.quantity;
        } else {
            subtotal = this.getOriginalPrice() * this.quantity;
        }

        if (this.siteCurrency === "TWD") {
            subtotal = Math.round(subtotal * 100) / 100;
        }
        return subtotal;
    }

    getRegularPrice() {
        return this.product.regular_price;
    }

    setQuantity(quantity) {
        this.quantity = quantity;
        this.product.quantity = quantity;
    }

    getDisplayUnitPrice() {
        return this.getPrice();
    }
    getDisplayRegularPrice() {
        return this.getOriginalPrice();
    }

    calculateComplexDiscount(complexString, result) {
        const tokens = complexString.split(/([\+\-\%])/);
        let operator = "+";
        let totalDiscount = new Decimal(0);
        result = new Decimal(result);

        tokens.forEach((token, key) => {
            if (!isNaN(token) && token !== "") {
                let currentDiscount = new Decimal(token);
                if (key + 1 < tokens.length && tokens[key + 1] === "%") {
                    let discountPercentage = currentDiscount.div(100);
                    currentDiscount = result.mul(discountPercentage);

                    if (this.roundLineItemDiscount) {
                        currentDiscount = currentDiscount.toDecimalPlaces(
                            this.siteCurrencyDecimalPlaces,
                            Decimal.ROUND_HALF_UP
                        );
                    }
                } else {
                    currentDiscount = currentDiscount.mul(this.quantity);
                }
                switch (operator) {
                    case "-":
                        result = result.add(currentDiscount);
                        totalDiscount = totalDiscount.sub(currentDiscount);
                        break;
                    case "%":
                        break;
                    default:
                        result = result.sub(currentDiscount);
                        totalDiscount = totalDiscount.add(currentDiscount);
                        break;
                }
            } else {
                operator = token;
            }
        });

        return totalDiscount;
    }
}

export class VariableCartItem extends CartItem {
    _type = "variable_cart_item";

    selectUom(uom) {
        this.product.selectUom(uom);
        this.product_id = this.product.selected_variation_id;
    }
}

export class BundleCartItem extends CartItem {
    _type = "bundle_cart_item";

    composite_children = {};
    component_children_map = {};
    subrecords = {};

    getWeight() {
        let totalWeight = 0;
        Object.values(this.composite_children).forEach((item) => (totalWeight += item.getWeight() * item.quantity));
        return totalWeight;
    }

    getVolume() {
        let totalVolume = 0;
        Object.values(this.composite_children).forEach((item) => (totalVolume += item.getVolume() * item.quantity));
        return totalVolume;
    }

    getDimensionalSum() {
        let totalDimensionalSum = 0;
        Object.values(this.composite_children).forEach(
            (item) => (totalDimensionalSum += item.getDimensionalSum() * item.quantity)
        );
        return totalDimensionalSum;
    }

    getPrice() {
        if (!this.product.per_item_pricing) {
            return this.product.regular_price;
        }

        let total = 0;
        Object.values(this.composite_children).forEach((item) => (total += item.getPrice()));
        return total;
    }

    // getDisplayRegularPrice() {
    //     let total = 0;
    //     Object.values(this.composite_children).forEach((item) => {
    //         let quantity = item.quantity / item.ratio;
    //         total += item.display_subtotal / quantity;
    //     });
    //     return total;
    // }

    // getDisplayUnitPrice() {
    //     let total = 0;
    //     Object.values(this.composite_children).forEach((item) => {
    //         let quantity = item.quantity / item.ratio;
    //         total += item.display_total / quantity;
    //     });
    //     return total;
    // }

    getDisplayRegularPrice() {
        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getOriginalPrice() * item.quantity;
        });
        return total;
    }

    getDisplayUnitPrice() {
        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getPrice() * item.quantity;
        });
        return total;
    }

    getDisplayTotal() {
        return this.getDisplayUnitPrice() * this.quantity;
    }

    // getDisplayTotalTax() {
    //     let total = 0;
    //     Object.values(this.composite_children).forEach((item) => {
    //         total += item.display_total_tax;
    //     });
    //     return total * this.quantity;
    // }

    getOriginalPrice() {
        if (!this.product.per_item_pricing) {
            return this.product.regular_price;
        }

        // follow component product price
        return this.getSubtotal();
    }

    getTotal() {
        if (!this.product.per_item_pricing) {
            return this.product.regular_price * this.quantity;
        }

        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getTotal();
        });

        return parseFloat(total.toFixed(2));
    }

    getSubtotal() {
        if (!this.product.per_item_pricing) {
            return this.product.regular_price * this.quantity;
        }

        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getSubtotal();
        });

        return parseFloat(total.toFixed(2));
    }

    getSubtotalTax() {
        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getSubtotalTax();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }

    getTotalTax() {
        let total = 0;
        Object.values(this.composite_children).forEach((item) => {
            total += item.getTotalTax();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }

    setQuantity(quantity) {
        this.quantity = quantity;
        this.product.quantity = quantity;
        // Object.values(this.composite_children).forEach((item) => item.setQuantity(item.ratio * this.quantity));
    }

    getComponentChild(component_id, product_id) {
        return this.getComponent(component_id)?.[product_id];
    }

    getComponent(component_id) {
        return this.component_children_map[component_id];
    }

    getComponentItem(component_id, variation_id) {
        return Object.values(this.composite_children).find(
            (record) => record.product_id === variation_id && record.component_id === component_id
        );
    }

    getComponentItemQuantity(component_id, variation_id) {
        let components = Object.values(this.composite_children).filter(
            (record) => record.product_id === variation_id && record.component_id === component_id
        );

        let totalQuantity = components.reduce((sum, record) => {
            return sum + (record.product?.quantity || 0);
        }, 0);

        return totalQuantity;
    }
}

export class ChildBundleCartItem extends CartItem {
    _type = "child_bundle_cart_item";

    composite_parent_id;
    component_id;
    component;
    ratio;
    composite_parent_quantity;

    updatePrice() {
        this.setSubtotalAndSubtotalTax();
        this.setTotalAndTotalTax();

        this.setDisplayTotal();
        this.setDisplayTotalTax();
        this.setDisplaySubtotal();
        this.setDisplaySubtotalTax();
        this.setDisplayRegularPrice();
        this.setDisplayUnitPrice();

        // console.log("quantity " + this.quantity);
        // console.log("regular_price " + this.regular_price);
        // console.log("regular_tax " + this.regular_tax);
        // console.log("unit_price " + this.unit_price);
        // console.log("unit_tax " + this.unit_tax);
        // console.log("subtotal " + this.subtotal);
        // console.log("subtotal_tax " + this.subtotal_tax);
        // console.log("total " + this.total);
        // console.log("total_tax " + this.total_tax);

        // console.log("display_total " + this.display_total);
        // console.log("display_total_tax " + this.display_total_tax);
        // console.log("display_unit_tax " + this.display_unit_tax);
        // console.log("display_subtotal " + this.display_subtotal);
        // console.log("display_subtotal_tax " + this.display_subtotal_tax);
        // console.log("display_regular_tax " + this.display_regular_tax);
        // console.log("display_regular_price " + this.display_regular_price);
        // console.log("display_unit_price " + this.display_unit_price);
        // console.log("===========================");
    }

    setSubtotalAndSubtotalTax() {
        this.subtotal = this.getSubtotal();
        this.subtotal_tax = this.getSubtotalTax();
    }

    setTotalAndTotalTax() {
        this.total = this.getTotal();
        this.total_tax = this.getTotalTax();
    }

    getDisplayPriceAfterTax(price, tax) {
        let percentage = this.tax_class?.percentage;
        if (!percentage || this.isTaxExempted || this.isTaxIncluded == this.showTax) {
            return price;
        } else if (this.isTaxIncluded && !this.showTax) {
            return price;
        } else {
            // !this.isTaxIncluded && this.showTax
            return price + tax;
        }
    }
    setDisplayRegularPrice() {
        this.display_regular_price = this.display_subtotal / this.quantity;
    }
    setDisplayUnitPrice() {
        this.display_unit_price = this.display_total / this.quantity;
    }
    setDisplaySubtotal() {
        this.display_subtotal = this.getDisplayPriceAfterTax(this.subtotal, this.total_tax);
    }
    setDisplaySubtotalTax() {
        this.display_subtotal_tax = this.subtotal - this.display_subtotal;
        this.display_regular_tax = this.display_subtotal_tax / this.quantity;
    }
    setDisplayTotal() {
        this.display_total = this.getDisplayPriceAfterTax(this.total, this.total_tax);
    }
    setDisplayTotalTax() {
        this.display_total_tax = this.total - this.display_total;
        this.display_unit_tax = this.display_total_tax / this.quantity;
    }

    getTotal() {
        return this.getPrice() * this.quantity * this.composite_parent_quantity;
    }

    getTotalWithoutTax() {
        if (this.isTaxExempted) {
            return this.getTotal();
        } else {
            return this.removeTaxIfNeeded(this.getTotal());
        }
    }

    getSubtotal() {
        return this.getOriginalPrice() * this.quantity * this.composite_parent_quantity;
    }

    getSubtotalWithoutTax() {
        if (this.isTaxExempted) {
            return this.getSubtotal();
        } else {
            return this.removeTaxIfNeeded(this.getSubtotal());
        }
    }

    /* seems not used
    getSubtotalTaxForSubmission(quantity) {
        let price_before_tax = this.subtotal_tax * quantity;

        return price_before_tax;
    }

    getTotalTaxForSubmission(quantity) {
        let price_before_tax = this.total_tax * quantity;

        return price_before_tax;
    }
    */

    // getPrice() {
    //     return this.component.calculateTotal(this.product.getCurrentPrice() * this.ratio, this.ratio);
    // }

    // getChildItemPrice() {
    //     return this.getPrice() / this.ratio;
    // }

    getOriginalPrice() {
        if (this.line_item) {
            if (this.mode == "return") {
                return parseFloat(this.addOrRemoveTaxIfNeeded(this.line_item.price));
            } else {
                return this.line_item.price;
            }
        }

        // let originalPrice = this.product.getOriginalRegularPrice();
        let originalPrice = this.product ? this.product.getOriginalPrice() : 0;
        if (this.component?.price !== null && this.component?.price !== undefined) {
            originalPrice = this.component.price;
        }

        return parseFloat(this.addOrRemoveTaxIfNeeded(originalPrice));
    }

    getCurrentPrice() {
        if (this.line_item) {
            if (this.mode == "return") {
                return parseFloat(this.addOrRemoveTaxIfNeeded(this.line_item.price));
            } else {
                return this.line_item.price;
            }
        }

        // let price = this.product.getOriginalRegularPrice(); // cuz currently still not applying bundle TA
        let price = this.product ? this.product.getCurrentPrice() : 0;

        if (this.isFreeQuantity) {
            price = 0;
            return parseFloat(this.addOrRemoveTaxIfNeeded(price));
        }

        if (this.component?.discount_percentage) {
            price -= (price * this.component.discount_percentage) / 100;
        } else if (this.component?.discount_amount) {
            price -= this.component.discount_amount;
        } else if (this.component?.price !== null && this.component?.price !== undefined) {
            price = this.component.price;
        }

        return parseFloat(this.addOrRemoveTaxIfNeeded(price));
    }
}

export class CartMapperParameter extends Parameter {
    cartMapper;
    mediaMapperParameter = new MediaMapperParameter();
    cartItemMapperParameter = new CartItemMapperParameter();
}

export class CartItemMapperParameter extends Parameter {
    cartItemMapper;
    productMapperParameter = new ProductMapperParameter();
    documentLineItemMapperParameter = new DocumentLineItemMapperParameter();
}

export class CartFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {Object, null} data Data to construct the Cart object with. Also creates a CartItem object using MetaFactory if data is not null.
     * @returns {Notification}
     */
    static construct(cartMapperParameter, data = null) {
        if (data != null) {
            let cart = DataObjectFactory.getInstance().constructWithMapper(Cart, cartMapperParameter.cartMapper, data);
            if (data.items != null) {
                let items = {};
                data.items.forEach((item) => {
                    const cartItem = CartItemFactory.construct(cartMapperParameter.cartItemMapperParameter, item);
                    items[cartItem.id] = cartItem;
                });

                cart.items = items;
            }

            if (data.attachments != null) {
                let attachments = {};
                data.attachments.forEach((attachmentData) => {
                    const attachment = MediaFactory.construct(cartMapperParameter.mediaMapperParameter, attachmentData);
                    attachments[attachment.id] = attachment;
                });

                cart.attachments = attachments;
            }

            if (data.return_attachments != null) {
                let returnAttachments = {};
                data.return_attachments.forEach((returnAttachmentData) => {
                    const returnAttachment = MediaFactory.construct(
                        cartMapperParameter.mediaMapperParameter,
                        returnAttachmentData
                    );
                    returnAttachments[returnAttachment.id] = returnAttachment;
                });

                cart.return_attachments = returnAttachments;
            }

            if (data.custom_fields) {
                const custom_fields = {};
                data.custom_fields.forEach((customField) => {
                    custom_fields[customField.mode + "_" + customField.label] = customField.value;
                });
                cart.custom_fields = custom_fields;
            }

            Object.values(cart.items).forEach((item) => {
                if (item instanceof ChildBundleCartItem) {
                    let parentItem = cart.items[item.composite_parent_id];
                    if (typeof parentItem !== "undefined") {
                        item.component = parentItem.product?.components?.[item.component_id];
                        // item.setQuantity(parentItem.quantity * item.ratio);
                        // Initialize the parent quantity at link time so getTotal()/getSubtotal() never
                        // see an undefined composite_parent_quantity before the first update pass runs.
                        item.composite_parent_quantity = parentItem.quantity;
                        parentItem.composite_children[item.id] = item;

                        if (parentItem.component_children_map[item.component_id] == null) {
                            parentItem.component_children_map[item.component_id] = {};
                        }

                        let componentChildMap = parentItem.component_children_map[item.component_id];
                        componentChildMap[item.product.id] = item;

                        if (typeof cart.items[item.composite_parent_id].subrecords == "undefined") {
                            cart.items[item.composite_parent_id].subrecords = [];
                        }

                        // this.$set(cart.items[item.composite_parent_id].subrecords, item.id, item);
                        item["primary_key"] = item.id;
                        cart.items[item.composite_parent_id].subrecords[item.id] = item;
                        cart.items[item.composite_parent_id].subrecordVisibility = true;
                    }

                    delete cart.items[item.id];
                }
            });

            Object.values(cart.items).forEach((item) => {
                if (typeof item.subrecords != "undefined" && Object.keys(item.subrecords).length > 0) {
                    item.subrecords = Object.values(item.subrecords);
                }
            });

            return cart;
        }

        return null;
    }
}

export class CartItemFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {Object, null} data Data to construct the CartItem object with. Creates Product objects internally too.
     * @returns {Notification}
     */
    static construct(cartItemMapperParameter, data = null) {
        if (data != null) {
            let constructor = CartItem;
            const product = ProductFactory.construct(cartItemMapperParameter.productMapperParameter, data.product);

            if (data.composite_parent_id != null) {
                constructor = ChildBundleCartItem;
            } else if (product instanceof VariableProduct) {
                constructor = VariableCartItem;
            } else if (product instanceof BundleProduct) {
                constructor = BundleCartItem;
            }

            let cartItem = DataObjectFactory.getInstance().constructWithMapper(
                constructor,
                cartItemMapperParameter.cartItemMapper,
                data
            );

            cartItem.product = product;
            cartItem.product.quantity = cartItem.quantity;

            if (product instanceof VariableProduct) {
                cartItem.product.selectVariation(cartItem.product_id);
            }

            if (data.line_item != null) {
                cartItem.line_item = DocumentLineItemFactory.construct(
                    cartItemMapperParameter.documentLineItemMapperParameter,
                    data.line_item
                );
            }

            return cartItem;
        }

        return null;
    }
}
